import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, ArrowRight, PhoneCall } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { Logo } from "@/components/shared/Logo";
import { NAV_LINKS, COMPANY } from "@/data/content";
import { useConsultation } from "@/components/forms/ConsultationDialog";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const { open } = useConsultation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 ${
        scrolled ? "border-b border-border/80 bg-background/85 backdrop-blur-xl" : "border-b border-transparent bg-transparent"
      }`}
    >
      <nav className="section-x">
        <div className="container-tmt flex h-28 items-center justify-between sm:h-32 lg:h-36">
          <Logo className="translate-y-1.5" imgClassName="h-24 w-auto sm:h-28 lg:h-32" />

          <div className="hidden items-center gap-1 lg:flex">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `rounded-md px-3.5 py-2 text-sm font-medium transition-colors ${
                    isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>

          <div className="hidden items-center gap-3 lg:flex">
            <a href={COMPANY.phoneHref} className="inline-flex items-center gap-2 font-mono text-sm text-muted-foreground transition-colors hover:text-primary">
              <PhoneCall className="h-4 w-4" /> {COMPANY.phone}
            </a>
            <Button variant="hero" size="default" onClick={open}>
              Book Consultation <ArrowRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Mobile */}
          <div className="lg:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open menu" className="h-14 w-14 [&_svg]:size-9">
                  <Menu />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[86%] max-w-sm border-border bg-card">
                <div className="mb-8 mt-2"><Logo imgClassName="h-20 w-auto" /></div>
                <div className="flex flex-col gap-1">
                  {NAV_LINKS.map((l) => (
                    <SheetClose asChild key={l.to}>
                      <NavLink
                        to={l.to}
                        className={({ isActive }) =>
                          `rounded-lg px-4 py-3 text-base font-medium transition-colors ${
                            isActive ? "bg-primary/10 text-primary" : "text-foreground hover:bg-secondary"
                          }`
                        }
                      >
                        {l.label}
                      </NavLink>
                    </SheetClose>
                  ))}
                </div>
                <div className="mt-8 space-y-3">
                  <SheetClose asChild>
                    <Button variant="hero" size="lg" className="w-full" onClick={open}>
                      Book Consultation <ArrowRight className="h-4 w-4" />
                    </Button>
                  </SheetClose>
                  <a href={COMPANY.phoneHref} className="flex items-center justify-center gap-2 font-mono text-sm text-muted-foreground">
                    <PhoneCall className="h-4 w-4" /> {COMPANY.phone}
                  </a>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </header>
  );
};
